// src/pages/ResetPassword.js
import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  useColorModeValue,
  useToast,
  FormErrorMessage,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import SEO from "../../components/SEO";

export default function ResetPassword() {
  // steps: "email" -> "otp-password" -> "done"
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const toast = useToast();
  const navigate = useNavigate();

  const API_BASE = process.env.REACT_APP_API_URL || "https://localhost:7211";
  const API = `${API_BASE}/api/auth`;

  // regex: at least 1 lowercase, 1 uppercase, 1 number, 1 special char, 8+ chars
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;
  const isPasswordValid = passwordRegex.test(password);

  // STEP 1 — Request Reset OTP
  const handleRequestOtp = async () => {
    if (!email.trim()) {
      toast({
        title: "Missing email",
        description: "Please enter your email.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`${API}/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      await res.json().catch(() => ({}));
      toast({
        title: "Check your email",
        description: "If the account exists, an OTP was sent.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      setStep("otp-password");
    } catch {
      toast({
        title: "Network error",
        description: "Could not reach the server.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 2 — Submit OTP + New Password
  const handleResetPassword = async () => {
    if (!otp.trim() || !password.trim()) {
      toast({
        title: "Missing fields",
        description: "Enter both OTP and your new password.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!isPasswordValid) {
      toast({
        title: "Weak password",
        description:
          "Password must contain 8+ characters, 1 uppercase, 1 lowercase, 1 number, and 1 special character.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`${API}/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          token: otp.trim(),
          password: password.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast({
          title: "Reset failed",
          description: data.message || "Invalid OTP or password.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (data.access_token) {
        localStorage.setItem("token", data.access_token);
        if (data.refresh_token) {
          localStorage.setItem("refreshToken", data.refresh_token);
        }
      }

      toast({
        title: "Password updated",
        description: "You can now log in with your new password.",
        status: "success",
        duration: 2500,
        isClosable: true,
      });

      setStep("done");
      navigate("/login");
    } catch {
      toast({
        title: "Network error",
        description: "Could not reach the server.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Reset Password - SkillBack"
        description="Reset your SkillBack password. Enter your email to receive a secure reset link and regain access to your tech resume builder."
        keywords="SkillBack password reset, forgot password, account recovery"
        canonical="https://skillback.io/ForgotPassword"
      />

      <Container display="flex" alignItems="flex-start" justifyContent="center">
      <Box
        mt="0"
        w={{ base: "90%", sm: "340px" }}
        p={{ base: "1.25rem", sm: "2rem" }}
        borderRadius="xl"
        boxShadow="md"
        bg={useColorModeValue("white", "gray.800")}
      >
        <Stack spacing="1.2rem" align="stretch">
          <Heading size="md" textAlign="center" mb="0.5rem">
            {step === "email"
              ? "Reset Password"
              : step === "otp-password"
              ? "Enter OTP & New Password"
              : "Success"}
          </Heading>

          {/* Step 1: Email */}
          {step === "email" && (
            <FormControl id="email">
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                placeholder="your@email.com"
                size="sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormControl>
          )}

          {/* Step 2: OTP + Password */}
          {step === "otp-password" && (
            <>
              <FormControl id="otp">
                <FormLabel>OTP</FormLabel>
                <Input
                  type="text"
                  placeholder="Enter OTP code"
                  size="sm"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                />
              </FormControl>
              <FormControl
                id="password"
                isInvalid={password.length > 0 && !isPasswordValid}
              >
                <FormLabel>New Password</FormLabel>
                <Input
                  type="password"
                  placeholder="Enter new password"
                  size="sm"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {!isPasswordValid && password.length > 0 && (
                  <FormErrorMessage>
                    Must contain 8+ chars, 1 uppercase, 1 lowercase, 1 number, 1
                    special character.
                  </FormErrorMessage>
                )}
              </FormControl>
            </>
          )}

          {/* Action Button */}
          {step !== "done" && (
            <Button
              type="button"
              onClick={
                step === "email" ? handleRequestOtp : handleResetPassword
              }
              colorScheme="blue"
              size="sm"
              w="full"
              mt="0.5rem"
              isLoading={isLoading}
            >
              {step === "email" ? "Send Reset OTP" : "Reset Password"}
            </Button>
          )}
        </Stack>
      </Box>
    </Container>
    </>
  );
}
